import { defineStore } from 'pinia'
import { computed, nextTick, ref, watchEffect } from 'vue'
import axiosInstance from '@/helpers/axios'
import { SwalSuccess, SwalError, SwalDelete } from '@/helpers/mySwal'
import { useJobStore } from './job'
import dayjs from 'dayjs'
import { useJobDuration } from '@/composables/useJobDuration' // Import the new composable

export const useVisitStore = defineStore('visit', () => {
  const jobStore = useJobStore()
  const visits = ref([])
  const visit = ref({})
  const loading = ref(false)
  const isLoaded = ref(false)
  const isLoadedOne = ref(false)

  // סטייט עבור התראה ורשימת ביקורים מאחרים
  const overdueCount = ref(0)
  const isShowingOverdueList = ref(false)
  // מביא את כמות הביקורים המתוכננים שעבר זמנם
  const fetchOverdueVisitsCount = async () => {
    try {
      const { data } = await axiosInstance.get('/api/visits/overdue-count')
      overdueCount.value = data.count || 0
    } catch (error) {
      overdueCount.value = 0
      console.error(error)
      SwalError.fire({
        title: 'טעינת כמות ביקורים מתוכננים שעבר זמנם נכשלה'
      })
    }
  }

  const statusOptions = ref(['מתוכנן', 'בתהליך', 'מושהה', 'בוצע', 'בוטל'])

  const liveDuration = ref('00:00:00') // משך זמן של הביקור הנבחר

  const getStatusSeverity = (status) => {
    switch (status) {
      case 'מתוכנן':
        return 'info' // סטטוס תכנון, אינפורמטיבי
      case 'בתהליך':
        return 'contrast' // סטטוס פעיל, בולטות מסוימת
      case 'מושהה':
        return 'warn' // סטטוס הדורש תשומת לב, אזהרה
      case 'בוצע':
        return 'success' // סטטוס הצלחה
      case 'בוטל':
        return 'danger' // סטטוס שלילי, סכנה או כישלון
      default:
        return 'secondary' // ברירת מחדל לסטטוסים לא מוכרים
    }
  }

  const getAllVisits = async () => {
    if (loading.value) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      await getAllVisits()
    } else if (!isLoaded.value) {
      await readAllVisits()
    }
  }

  const readAllVisits = async ({ start, end, toCalendar } = {}) => {
    loading.value = true
    try {
      const params = { start, end }
      const { data } = await axiosInstance.get('/api/visits', { params })

      if (toCalendar) {
        return data
      }
      visits.value = data
      // עדכן isLoaded רק אם לא בוצע סינון
      if (!start && !end) {
        isLoaded.value = true
      }
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת ביקורים נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const getOneJobVisits = async (jobId) => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get(`/api/visits/job/${jobId}`)
      visits.value = data
      isLoaded.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת ביקורים נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const getOneVisit = async (id) => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get(`/api/visits/${id}`)
      visit.value = data
      isLoadedOne.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת ביקור נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const createVisit = async (data) => {
    loading.value = true
    try {
      const { data: visit } = await axiosInstance.post('/api/visits', data)
      if (isLoaded.value) {
        visits.value.push(visit)
      }

      jobStore.isLoaded = false

      SwalSuccess.fire({
        title: 'ביקור חדש נוצר בהצלחה'
      })

      return visit
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'הוספת ביקור נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const updateVisit = async (data) => {
    loading.value = true
    try {
      const { data: updatedVisit } = await axiosInstance.put(`/api/visits/${data.id}`, data)

      // עדכן תמיד את visits אם הביקור קיים, אחרת הוסף אותו
      const idx = visits.value.findIndex((visit) => visit.id === data.id)
      if (idx > -1) {
        visits.value[idx] = updatedVisit
      } else if (visits.value.length > 0) {
        visits.value.push(updatedVisit)
      }

      if (isLoadedOne.value?.id === data.id) {
        visit.value = updatedVisit
      }

      // עדכון ידני של הביקור בתוך רשימת העבודות הראשית
      if (jobStore.isLoaded) {
        const jobToUpdate = jobStore.jobs.find((j) => j.id === updatedVisit.jobId)
        if (jobToUpdate && jobToUpdate.visits) {
          const visitIndex = jobToUpdate.visits.findIndex((v) => v.id === updatedVisit.id)
          if (visitIndex > -1) {
            // החלף את הביקור הישן בחדש
            jobToUpdate.visits[visitIndex] = { ...jobToUpdate.visits[visitIndex], ...updatedVisit }
          }
        }
      }

      // אם הביקור הוא מתוך דף ניהול עבודה, עדכן את הביקור בדף הניהול
      if (jobStore.job?.visits) {
        const visitIndex = jobStore.job.visits.findIndex((v) => v.id === updatedVisit.id)
        if (visitIndex > -1) {
          // החלף את הביקור הישן בחדש
          jobStore.job.visits[visitIndex] = { ...jobStore.job.visits[visitIndex], ...updatedVisit }
        }
      }

      SwalSuccess.fire({
        title: 'פרטי הביקור עודכנו בהצלחה'
      })

      return updatedVisit
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'עדכון ביקור נכשל'
      })
    } finally {
      loading.value = false
    }
  }

  const deleteVisit = async (id, { jobIdToRefresh }) => {
    loading.value = true
    try {
      await axiosInstance.delete(`/api/visits/${id}`)
      if (isLoaded.value) {
        visits.value = visits.value.filter((visit) => visit.id !== id)
      }

      if (jobIdToRefresh) {
        await jobStore.getOneJob(jobIdToRefresh)
      }

      jobStore.isLoaded = false

      SwalSuccess.fire({
        title: 'פרטי הביקור נמחקו בהצלחה'
      })
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'מחיקת ביקור נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const confirmDelete = async (id, { jobIdToRefresh }) => {
    const confirm = await SwalDelete.fire({
      text: `האם אתה בטוח שברצונך למחוק את הביקור?`
    })

    if (confirm.isConfirmed) {
      await deleteVisit(id, { jobIdToRefresh })
    }
  }

  const startVisit = async (id) => {
    const now = new Date()
    const data = {
      id,
      status: 'בתהליך',
      actualStartTime: now
    }
    await updateVisit(data)
  }

  const resetVisit = async (id) => {
    const data = {
      id,
      status: 'מתוכנן',
      actualStartTime: null,
      actualEndTime: null,
      totalPausedDuration: 0
    }
    await updateVisit(data)
  }

  const pauseVisit = async (id) => {
    const data = {
      id,
      status: 'מושהה',
      lastPauseTime: new Date()
    }

    await updateVisit(data)
  }

  // המשך ביקור
  const resumeVisit = async (currentVisit) => {
    const now = dayjs()
    const startPause = dayjs(currentVisit.lastPauseTime)

    const duration = now.diff(startPause, 'seconds')
    const data = {
      id: currentVisit.id,
      status: 'בתהליך',
      lastPauseTime: null,
      totalPausedDuration: currentVisit.totalPausedDuration + duration
    }
    await updateVisit(data)
  }

  /**
   * סיום ביקור.
   * במידה והביקור היה במצב מושהה, זמן הסיום יהיה זהה לזמן השהיה האחרונה
   * @param {Object} currentVisit - הביקור הנוכחי
   * @return {Promise<void>}
   */
  const endVisit = async (currentVisit) => {
    const data = {
      id: currentVisit.id,
      status: 'בוצע'
    }

    // עדכון זמן סיום רק אם הביקור היה בתהליך או מושהה
    if (currentVisit.status === 'בתהליך' || currentVisit.status === 'מושהה') {
      data.actualEndTime = currentVisit.status === 'מושהה' ? currentVisit.lastPauseTime : new Date()

      if (currentVisit.status === 'מושהה') {
        data.lastPauseTime = null
      }
    }

    await updateVisit(data)
  }

  /**
   * ביטול ביקור
   * @param {number} id - מזהה הביקור
   * @return {Promise<void>}
   */
  const cancelVisit = async (id) => {
    // התרעה לפני ביטול
    const confirm = await SwalDelete.fire({
      text: `האם אתה בטוח שברצונך לבטל את הביקור?`,
      title: 'ביטול ביקור',
      confirmButtonText: 'בטל ביקור'
    })

    if (!confirm.isConfirmed) return

    const data = {
      id,
      status: 'בוטל',
      previousStatusBeforeCancel:
        visits.value?.find((v) => v.id === id)?.status ||
        jobStore.job?.visits?.find((v) => v.id === id).status
    }
    await updateVisit(data)
  }

  // המשך ביקור לאחר שהסתיים
  const resumeCompletedVisit = async (currentVisit) => {
    // ודא שהביקור בסטטוס בוצע ויש לו זמן סיום בפועל
    if (currentVisit.status !== 'בוצע' || !currentVisit.actualEndTime) {
      SwalError.fire({
        title: 'לא ניתן להמשיך ביקור שלא הסתיים או שאין לו זמן סיום בפועל'
      })

      return
    }

    // חשב משך זמן מאז שהביקור סומן כבוצע
    const duration = dayjs().diff(dayjs(currentVisit.actualEndTime), 'seconds')
    const data = {
      id: currentVisit.id,
      status: 'בתהליך',
      actualEndTime: null,
      totalPausedDuration: currentVisit.totalPausedDuration + duration
    }
    await updateVisit(data)
  }

  /**
   * ביטול ביטול ביקור - מחזיר את הביקור לסטטוס הקודם לביטול
   * @param {object} currentVisit - הביקור הנוכחי
   * @return {Promise<void>}
   */
  const revertCancel = async (currentVisit) => {
    const data = {
      id: currentVisit.id,
      status: currentVisit.previousStatusBeforeCancel,
      previousStatusBeforeCancel: null
    }
    await updateVisit(data)
  }

  // קבל משך כולל של הביקור,
  // חשב מזמן התחלה בפועל עד זמן סיום בפועל או עד זמן התחלת השהיה אחרונה או עד זמן נוכחי
  // הפחת מזה את משך ההשהיות אם קיים
  // החזר מחרוזת מעוצבת HH:mm:ss
  const getVisitDuration = (currentVisit) => {
    // בדיקה אם currentVisit או actualStartTime קיימים
    if (!currentVisit || !currentVisit.actualStartTime) {
      return '00:00:00'
    }

    const from = dayjs(currentVisit.actualStartTime)
    let to
    if (currentVisit.actualEndTime) {
      to = dayjs(currentVisit.actualEndTime)
    } else if (currentVisit.lastPauseTime && currentVisit.status === 'מושהה') {
      // אם הביקור מושהה ויש זמן השהייה אחרון, השתמש בו
      to = dayjs(currentVisit.lastPauseTime)
    } else {
      // במצב "בתהליך", או כל מצב אחר ללא זמן סיום מוגדר, השתמש בזמן הנוכחי.
      // זה מאפשר לטיימר להתעדכן בזמן אמת.
      to = dayjs()
    }

    let secondsDuration = to.diff(from, 'seconds')
    if (currentVisit.totalPausedDuration) {
      secondsDuration -= currentVisit.totalPausedDuration
    }

    // ודא שמשך הזמן אינו שלילי
    if (secondsDuration < 0) {
      secondsDuration = 0
    }

    return dayjs.duration(secondsDuration, 'seconds').format('HH:mm:ss')
  }

  const $reset = () => {
    visits.value = []
    visit.value = {}
    loading.value = false
    isLoaded.value = false
    isLoadedOne.value = false
  }

  return {
    visits,
    visit,
    loading,
    isLoaded,
    isLoadedOne,
    statusOptions,
    liveDuration,
    getStatusSeverity,
    getAllVisits,
    readAllVisits,
    getOneJobVisits,
    getOneVisit,
    createVisit,
    updateVisit,
    deleteVisit,
    confirmDelete,
    startVisit,
    resetVisit,
    pauseVisit,
    resumeVisit,
    endVisit,
    cancelVisit,
    resumeCompletedVisit,
    revertCancel,
    getVisitDuration,
    $reset,
    // חדשים
    overdueCount,
    fetchOverdueVisitsCount,
    isShowingOverdueList
  }
})
